/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef, type MouseEvent } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './InvestmentScreeningLedger.module.scss';

const COLUMNS = [
  { key: 'us', label: 'Registry-first', name: 'Global Database', winner: true },
  { key: 'o1', label: 'Funding-first', name: 'PitchBook, Crunchbase', winner: false },
  { key: 'o2', label: 'Credit-first', name: 'D&B, Bureau van Dijk', winner: false },
] as const;

const ROWS = [
  {
    dim: 'Universe',
    us: 'Every registered company, 600M+ across 200+ countries',
    o1: 'Mainly companies that received funding',
    o2: 'Broad, weighted toward credit-scored trading entities',
  },
  {
    dim: 'Financials',
    us: 'Accounts as filed, in the filing currency, with the reporting period',
    o1: 'Mainly companies that received funding',
    o2: 'Blended and modelled, methodology published',
  },
  {
    dim: 'Source of a field',
    us: 'Named registry and filing date on every field',
    o1: 'Announcements, company submissions, editorial research',
    o2: 'Mixed: filings, trade experience, modelling',
  },
  {
    dim: 'Ownership',
    us: "Shareholder register and control chain built from each jurisdiction's own filings",
    o1: 'Cap table where the company disclosed it',
    o2: 'Corporate linkage, substantially modelled',
  },
  {
    dim: 'Identifiers',
    us: 'Registration number and VAT alongside our own ID',
    o1: 'Platform identifier',
    o2: 'Proprietary identifier',
  },
  {
    dim: 'Redistribution',
    us: 'Licensed into your own products, models and warehouse',
    o1: 'Restricted',
    o2: 'Restricted, typically per seat',
  },
  {
    dim: 'Refresh',
    us: 'Daily sync from source, change events on every watched name',
    o1: 'On announcement',
    o2: 'Varies by market and product',
  },
] as const;

const CheckIcon = () => (
  <span className={styles.checkChip}>
    <svg width="9" height="9" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M11.187.813a.5.5 0 0 1 0 .708L4.52 8.187a.5.5 0 0 1-.707 0L.48 4.854a.5.5 0 1 1 .707-.707l2.98 2.98L10.48.813a.5.5 0 0 1 .707 0"
        fill="currentColor"
      />
    </svg>
  </span>
);

const SparkIcon = () => (
  <svg width="9" height="9" viewBox="0 0 9 9" fill="none" aria-hidden="true">
    <path d="M4.5 0 5.4 3.6 9 4.5 5.4 5.4 4.5 9 3.6 5.4 0 4.5 3.6 3.6z" fill="currentColor" />
  </svg>
);

export const InvestmentScreeningLedger = () => {
  const { t } = useInt();
  const headRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);

  useEffect(() => {
    const revealables = [headRef.current, ...rowRefs.current].filter((el): el is HTMLDivElement | HTMLTableRowElement =>
      Boolean(el),
    );

    if (!revealables.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.revealed);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 },
    );

    revealables.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleSpotlight = (e: MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - rect.top}px`);
  };

  return (
    <Section background="#0E1526" className={styles.root}>
      <div className={styles.glow} />
      <div className={styles.glow2} />

      <div ref={headRef} className={cn(styles.head, styles.reveal)}>
        <TitleBlock
          theme="dark"
          align="left"
          extra={t('Methodology, compared')}
          title={t('Where the screen comes from')}
          description={t('Every platform is good at the thing it was built for. This is what each was built for.')}
        />
      </div>

      <div className={styles.tableWrap} onMouseMove={handleSpotlight}>
        <span className={styles.border} />
        <table className={styles.ledger}>
          <thead>
            <tr>
              <th />
              {COLUMNS.map((col) => (
                <th key={col.key} className={col.winner ? styles.us : undefined}>
                  {col.winner && (
                    <span className={styles.badge}>
                      <SparkIcon />
                      {t('Best fit for screening')}
                    </span>
                  )}
                  <span className={styles.label}>{t(col.label)}</span>
                  <span className={cn(styles.name, col.winner && styles.nameWinner)}>{col.name}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row, i) => (
              <tr
                key={row.dim}
                ref={(el) => {
                  rowRefs.current[i] = el;
                }}
                className={cn(styles.row, styles.reveal)}
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <td className={styles.dim}>
                  <span className={styles.index}>{String(i + 1).padStart(2, '0')}</span>
                  {t(row.dim)}
                </td>
                {COLUMNS.map((col) => (
                  <td key={col.key} className={col.winner ? styles.us : styles.other}>
                    <span className={styles.mobileLabel}>{col.name}</span>
                    <span className={styles.cellText}>
                      {col.winner ? <CheckIcon /> : <span className={styles.dot} />}
                      {t(row[col.key])}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <span className={styles.spotlight} />
        <span className={cn(styles.corner, styles.cornerTl)} />
        <span className={cn(styles.corner, styles.cornerBr)} />
      </div>
    </Section>
  );
};
